import { z } from 'zod'
import type {
  BookCheckArea,
  BookCheckCode,
  BookCheckSeverity,
  BookFindingParam,
} from './accounting-health.js'
import { optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { ModuleKey, Permission } from './permissions.js'
import { SIGNAL_CODES } from './project-insights.js'
import { purchaseCaptureSchema } from './purchasing.js'
import { serviceSiteCaptureSchema } from './service-assets.js'

/**
 * Core module `ai` (P8; docs/refactor/projects/05-insights-and-ai.md Part B, ADR-041, P8_BREAKDOWN.md).
 * An assistant that runs with each company's own OpenAI key, inside the user's own request: every tool
 * call goes through the same services, guards, scopes and RLS as the screens. It reads with tools and
 * changes nothing without a click on a proposal card. Off by default, per company and per feature.
 */

const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)

/** At most this many characters of scope text for `projects.plan`. */
export const AI_PLAN_SCOPE_MAX = 8000

/**
 * A `projects.plan` message: the line `Start: YYYY-MM-DD` (the date the plan's working-day offsets count
 * from, chosen on the screen), a blank line, then the scope.
 */
export const aiPlanMessage = (start: string, scope: string): string => `Start: ${start}\n\n${scope}`

/** A message's limit: a plan's full scope with its start line. */
export const AI_MESSAGE_MAX = AI_PLAN_SCOPE_MAX + 32

// ── features and subjects ────────────────────────────────────────────────────

/** Each needs its flag in the company's AI settings and the module's own switch (Projects: `assistant`). */
export const AI_FEATURES = [
  'projects.ask',
  'projects.status_draft',
  'projects.explain_signal',
  'projects.plan',
  'projects.boq_mapping',
  /** AA1 (ACCOUNTING_ASSISTANT.md §3.2): the accounting assistant. */
  'accounting.ask',
  'accounting.explain',
  'accounting.review',
  'accounting.coding',
  'accounting.setup',
  'accounting.commentary',
  /** IA1 (INVENTORY_ASSISTANT.md §3.2): the inventory assistant. */
  'inventory.ask',
  'inventory.explain',
  'inventory.review',
  'inventory.reorder',
  /** AC1 (ASSISTANT_CAPTURE.md §3.2): a purchase filled from a list or a supplier's bill. */
  'purchasing.capture',
  /** SA1 (SERVICE_ASSISTANT.md §3): sites, places and equipment, by asking (cards) or from a document. */
  'service.ask',
  'service.capture',
  /**
   * AE2 (ASSISTANT_EVERYWHERE.md D4): the company assistant behind the button on every page. It answers with
   * the tools of the other areas' `<area>.ask` features, loaded area by area.
   */
  'assistant.ask',
] as const
export type AiFeature = (typeof AI_FEATURES)[number]

/**
 * The areas the super admin allows per company (console AI policy `allowedAreas`) and the company switches in
 * Settings › AI assistant; every feature belongs to one.
 */
export const AI_AREAS = ['general', 'projects', 'accounting', 'inventory', 'purchasing', 'service'] as const
export type AiArea = (typeof AI_AREAS)[number]
export const AI_FEATURE_AREA: Record<AiFeature, AiArea> = {
  'assistant.ask': 'general',
  'projects.ask': 'projects',
  'projects.status_draft': 'projects',
  'projects.explain_signal': 'projects',
  'projects.plan': 'projects',
  'projects.boq_mapping': 'projects',
  'accounting.ask': 'accounting',
  'accounting.explain': 'accounting',
  'accounting.review': 'accounting',
  'accounting.coding': 'accounting',
  'accounting.setup': 'accounting',
  'accounting.commentary': 'accounting',
  'inventory.ask': 'inventory',
  'inventory.explain': 'inventory',
  'inventory.review': 'inventory',
  'inventory.reorder': 'inventory',
  'purchasing.capture': 'purchasing',
  'service.ask': 'service',
  'service.capture': 'service',
}
/** The features of an area, in catalogue order. */
export function aiAreaFeatures(area: AiArea): AiFeature[] {
  return AI_FEATURES.filter((f) => AI_FEATURE_AREA[f] === area)
}

/**
 * What a conversation is about; the tools are bound to it (the model can't name another record). `books`: the
 * company's books (the subject id is the company's id); `bank_statement`: one bank statement (AA1); `stock`: the
 * company's stock (the subject id is the company's id; IA1); `purchasing`: the company's purchasing (the subject
 * id is the company's id; AC1).
 */
export const AI_SUBJECT_TYPES = [
  'project',
  'books',
  'bank_statement',
  'stock',
  'purchasing',
  /** SA1: the company's service sites, places and equipment (the subject id is the company's id). */
  'service_sites',
  /** AE2: the whole company, for `assistant.ask` (the subject id is the company's id). */
  'company',
] as const
export type AiSubjectType = (typeof AI_SUBJECT_TYPES)[number]

/**
 * AE2 (ASSISTANT_EVERYWHERE.md D8): the areas whose tools the company assistant may load, each through its
 * `<area>.ask` feature, in the fixed order they are added to its instructions. Projects stays on project
 * conversations (its tools are bound to one project); Purchasing has no ask feature (capture only).
 */
export const AI_COMPANY_AREAS = ['inventory', 'accounting', 'service'] as const satisfies readonly AiArea[]
export type AiCompanyArea = (typeof AI_COMPANY_AREAS)[number]
/** At most this many areas are open in a company conversation; opening another closes the least recently used. */
export const AI_OPEN_AREAS_MAX = 3

/**
 * The pages of each area, by path prefix (`/stock` also covers `/stock/…`, not `/stock-levels`): a question
 * asked on one opens its area first.
 */
export const AI_AREA_PAGES: Record<AiCompanyArea, readonly string[]> = {
  inventory: [
    '/stock',
    '/stock-movements',
    '/stock-adjustments',
    '/stock-transfers',
    '/stock-valuation',
    '/low-stock',
    '/warehouses',
    '/products',
    '/product-categories',
    '/product-attributes',
    '/units',
    '/serials',
  ],
  accounting: [
    '/accounts',
    '/account-mapping',
    '/journal-entries',
    '/general-ledger',
    '/trial-balance',
    '/profit-and-loss',
    '/balance-sheet',
    '/cash-flow',
    '/vat-return',
    '/receivables-aging',
    '/payables-aging',
    '/books-health',
    '/bank-statements',
    '/cash-bank',
    '/fiscal-years',
    '/tax-rates',
    '/expenses',
    '/expense-categories',
  ],
  service: ['/service'],
}

/** The area a page belongs to (`AI_AREA_PAGES`), or null. */
export function aiPageArea(path: string): AiCompanyArea | null {
  for (const area of AI_COMPANY_AREAS) {
    if (AI_AREA_PAGES[area].some((p) => path === p || path.startsWith(`${p}/`))) return area
  }
  return null
}

/** The records a page may name to the company assistant (AE2): each module checks and labels its own. */
export const AI_PAGE_RECORD_TYPES = ['product', 'service_site', 'service_contract'] as const
export type AiPageRecordType = (typeof AI_PAGE_RECORD_TYPES)[number]
/** The company area each page record belongs to: the record is told only when its area is available. */
export const AI_PAGE_RECORD_AREA: Record<AiPageRecordType, AiCompanyArea> = {
  product: 'inventory',
  service_site: 'service',
  service_contract: 'service',
}

// ── the company assistant's lookups and pages (AE3; ASSISTANT_EVERYWHERE.md §7) ─────────────────────

/**
 * The records `find_records` and `get_record` reach, each through its module's own list and detail services
 * as the user (its module on, its route's permission). The names are the `ref:` types of the web's links.
 */
export const AI_RECORD_KINDS = [
  'sales_invoice',
  'quotation',
  'receipt',
  'credit_note',
  'customer',
  'supplier',
  'purchase',
  'supplier_payment',
  'expense',
  'product',
] as const
export type AiRecordKind = (typeof AI_RECORD_KINDS)[number]

/** The reports `run_report` runs, each through the Reports module's own service as the user. */
export const AI_REPORT_KINDS = [
  'sales_by_period',
  'sales_by_customer',
  'sales_by_product',
  'sales_by_salesperson',
  'purchases_by_supplier',
  'purchases_by_product',
] as const
export type AiReportKind = (typeof AI_REPORT_KINDS)[number]

/**
 * The AI area whose switches (the console's `allowedAreas`, the company's `areasOff`) also govern a record kind or
 * report: while that area is off, nothing of it reaches the model. Sales and customers have no area of their own:
 * the company assistant's own (`general`) governs them.
 */
export const AI_RECORD_KIND_AREA: Partial<Record<AiRecordKind, AiArea>> = {
  product: 'inventory',
  expense: 'accounting',
  supplier: 'purchasing',
  purchase: 'purchasing',
  supplier_payment: 'purchasing',
}
export const AI_REPORT_AREA: Partial<Record<AiReportKind, AiArea>> = {
  purchases_by_supplier: 'purchasing',
  purchases_by_product: 'purchasing',
}

/**
 * A page the company assistant may open (`open_page`), as the sidebar shows it: its path, its English name for
 * the model, and who sees it (`permission`: every one of them; `anyPermission`: at least one; `module`: on).
 * The web's nav test keeps each in step with its sidebar entry.
 */
export interface AiPage {
  path: string
  label: string
  permission?: readonly Permission[]
  anyPermission?: readonly Permission[]
  module?: ModuleKey
}

const page = (
  path: string,
  label: string,
  rule: { permission?: Permission[]; anyPermission?: Permission[]; module?: ModuleKey } = {},
): AiPage => ({ path, label, ...rule })

export const AI_PAGES: readonly AiPage[] = [
  page('/', 'Home'),
  page('/my-work', 'My work', { permission: ['tasks.read'], module: 'tasks' }),
  page('/tasks', 'Tasks', { permission: ['tasks.read'], module: 'tasks' }),
  page('/customers', 'Customers', { permission: ['customers.read'] }),
  page('/quotations', 'Quotations', { permission: ['sales.read'] }),
  page('/invoices', 'Invoices', { permission: ['sales.read'] }),
  page('/credit-notes', 'Credit notes', { permission: ['sales.read'] }),
  page('/receipts', 'Receipts', { permission: ['sales.read'] }),
  page('/refunds', 'Refunds', { permission: ['sales.read'] }),
  page('/receivables-aging', 'Receivables aging', { permission: ['sales.read'] }),
  page('/suppliers', 'Suppliers', { permission: ['suppliers.read'] }),
  page('/purchases', 'Purchases', { permission: ['purchasing.read'] }),
  page('/supplier-returns', 'Supplier returns', { permission: ['purchasing.read'] }),
  page('/supplier-payments', 'Supplier payments', { permission: ['purchasing.read'] }),
  page('/payables-aging', 'Payables aging', { permission: ['purchasing.read'] }),
  page('/expenses', 'Expenses', { permission: ['expenses.read'] }),
  page('/expense-categories', 'Expense categories', { permission: ['expenses.read'] }),
  page('/products', 'Products', { permission: ['products.read'] }),
  page('/product-categories', 'Product categories', { permission: ['products.read'] }),
  page('/warehouses', 'Warehouses', { permission: ['inventory.read'] }),
  page('/stock', 'Stock levels', { permission: ['inventory.read'] }),
  page('/stock-movements', 'Stock movements', { permission: ['inventory.read'] }),
  page('/stock-adjustments', 'Stock adjustments', { permission: ['inventory.read'] }),
  page('/stock-transfers', 'Stock transfers', { permission: ['inventory.read'] }),
  page('/journal-entries', 'Journal entries', { permission: ['ledger.read'] }),
  page('/cash-bank', 'Cash & bank', { permission: ['treasury.read'] }),
  page('/accounts', 'Chart of accounts', { permission: ['ledger.read'] }),
  page('/books-health', 'Books health', {
    anyPermission: ['ledger.read', 'reports.financial'],
    module: 'accounting',
  }),
  page('/trial-balance', 'Trial balance', { permission: ['reports.financial'] }),
  page('/profit-and-loss', 'Profit and loss', { permission: ['reports.financial'] }),
  page('/balance-sheet', 'Balance sheet', { permission: ['reports.financial'] }),
  page('/general-ledger', 'General ledger', { permission: ['reports.financial'] }),
  page('/vat-return', 'VAT return', { permission: ['reports.financial'] }),
  page('/cash-flow', 'Cash flow', { permission: ['reports.financial'] }),
  page('/projects', 'Projects', { permission: ['projects.read'], module: 'projects' }),
  page('/service', 'Service overview', { permission: ['service.read'], module: 'service' }),
  page('/service/jobs', 'Service jobs', { permission: ['service.read'], module: 'service' }),
  page('/service/contracts', 'Maintenance contracts', {
    permission: ['service.read', 'contracts.read'],
    module: 'contracts',
  }),
  page('/hr/employees', 'Employees', { permission: ['employees.read'], module: 'hr' }),
  page('/payroll/runs', 'Payroll runs', { permission: ['payroll.read'], module: 'payroll' }),
]

/** Whether a page is open to someone holding `permissions` in a company with `modules`. */
export function aiPageOpen(p: AiPage, permissions: ReadonlySet<string>, modules: readonly string[]): boolean {
  return (
    (!p.module || modules.includes(p.module)) &&
    (p.permission ?? []).every((x) => permissions.has(x)) &&
    (!p.anyPermission?.length || p.anyPermission.some((x) => permissions.has(x)))
  )
}

/** A filter value a list page shows (its URL's search), as the company assistant reads it. */
const pageFilterValue = z.union([z.string().max(100), z.number(), z.boolean()])

/**
 * AE2 (ASSISTANT_EVERYWHERE.md D6): the page the company assistant was asked on. Data from the screen, never
 * identity: the server keeps a record only when its module finds it visible to the user, and the model reads
 * all of it as untrusted.
 */
export const aiPageContextSchema = z.object({
  /** The page's path, e.g. `/products/0195…`. */
  path: z
    .string()
    .max(300)
    .regex(/^\/[A-Za-z0-9\-._~/%$]*$/, 'A path'),
  /** AE3: the page's title as the browser shows it (data, read as untrusted). */
  title: z.string().trim().max(120).optional(),
  /** The record the page shows. */
  record: z.object({ type: z.enum(AI_PAGE_RECORD_TYPES), id: z.uuid() }).optional(),
  /** The list's filters and sort, as its URL holds them (at most 12). */
  filters: z
    .record(z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,39}$/), pageFilterValue)
    .refine((f) => Object.keys(f).length <= 12, 'At most 12 filters')
    .optional(),
})
export type AiPageContext = z.infer<typeof aiPageContextSchema>

export const AI_LANGUAGES = ['en', 'ar'] as const
export type AiLanguage = (typeof AI_LANGUAGES)[number]

// ── company settings (`GET/PATCH /ai/settings`, ai.manage) ───────────────────

/** The version of the acknowledgement text (EN/AR in the web); a new version asks again. */
export const AI_ACKNOWLEDGEMENT_VERSION = 1
/**
 * AC1 (audit P7, owner decision D3): the acknowledgement a company accepts before turning on a feature that
 * reads documents (`AI_ATTACHMENT_FEATURES`): the general text with its "sent" point carved out, plus what such
 * a feature sends (the whole document) and keeps (nothing of it). Asked only then; the general one (version 1)
 * keeps every other feature working. Version 3 (AE4', ASSISTANT_EVERYWHERE.md §7.6) adds that what the
 * assistant on every page reads from a document stays with the conversation: its panel's documents need it,
 * while the capture features still run on version 2 (`AI_CAPTURE_ACKNOWLEDGEMENT_VERSION`).
 */
export const AI_DOCUMENTS_ACKNOWLEDGEMENT_VERSION = 3
/** The documents acknowledgement the capture features (AC1, SA1) run on: version 2 said all they do. */
export const AI_CAPTURE_ACKNOWLEDGEMENT_VERSION = 2

const stamp = z.object({ by: z.uuid(), at: z.iso.datetime({ offset: true }) })

/** The stored settings; the stamps are written by the API only. */
export const aiSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** Who accepted the acknowledgement (what is sent, under whose account) and when; null until enabled once. */
  acknowledgement: stamp
    .extend({ version: z.number().int().min(1) })
    .nullable()
    .default(null),
  /** Who accepted the documents acknowledgement (`AI_DOCUMENTS_ACKNOWLEDGEMENT_VERSION`) and when (AC1). */
  documentsAcknowledgement: stamp
    .extend({ version: z.number().int().min(1) })
    .nullable()
    .default(null),
  /** In the deployment's `AI_MODELS` and the console's allowed models; null: the first allowed. */
  model: z.string().trim().max(100).nullable().default(null),
  /** Tokens a month for the company; at most the console cap; null: the console cap. */
  monthlyTokenLimit: z.number().int().min(0).nullable().default(null),
  perUserDailyRuns: z.number().int().min(1).max(500).default(50),
  /**
   * AE5a (ASSISTANT_EVERYWHERE.md D12): the company assistant may propose everyday changes on cards (customers,
   * suppliers, products and prices, stock work, drafts). Each still needs the user's own permission and a Confirm.
   */
  assistantChanges: z.boolean().default(true),
  /**
   * AE5b (ADR-051): it may also propose sensitive changes (posting and voiding invoices, purchases and credit
   * notes, receipts, supplier payments, archiving); off by default, and only with `assistantChanges` on.
   */
  assistantSensitive: z.boolean().default(false),
  /**
   * AE4' (ASSISTANT_EVERYWHERE.md §7.6): it may read photos and PDFs sent in its panel; on by default, and only
   * with the documents acknowledgement (`documentsAcknowledgement`).
   */
  assistantDocuments: z.boolean().default(true),
  features: z
    .object(
      Object.fromEntries(AI_FEATURES.map((f) => [f, z.boolean().default(false)])) as Record<
        AiFeature,
        z.ZodDefault<z.ZodBoolean>
      >,
    )
    .default(Object.fromEntries(AI_FEATURES.map((f) => [f, false])) as Record<AiFeature, boolean>),
  /**
   * AA1 decision 34: the areas the company switched off. A feature is on only when its tick is on, its area
   * isn't here and the console allows the area; the ticks stay as they are, so switching back restores them.
   */
  areasOff: z.array(z.enum(AI_AREAS)).max(AI_AREAS.length).default([]),
  /** AC1: who turned each feature on and when, while it is on (stamped by the API; a feature turned off loses it). */
  featuresTurnedOn: z.partialRecord(z.enum(AI_FEATURES), stamp).default({}),
  /** Conversation text and paused states are purged this many days after their last activity. */
  retentionDays: z.number().int().min(1).max(365).default(30),
  /** An OM-zone company sends data to a GLOBAL provider only with this approval (§11.1), stamped. */
  providerZoneApproved: z.boolean().default(false),
  providerZoneApproval: stamp.nullable().default(null),
})
export type AiSettings = z.output<typeof aiSettingsSchema>
export const DEFAULT_AI_SETTINGS: AiSettings = aiSettingsSchema.parse({})

/**
 * `PATCH /ai/settings`: what an administrator sets. Turning `enabled` on the first time (or after a new
 * acknowledgement version) needs `acknowledged: true` in the same request (422 AI_ACK_REQUIRED); the API
 * stamps the acknowledgement and the zone approval. Fields omitted keep their value.
 */
export const aiSettingsPatchSchema = z.object({
  version: versionSchema,
  enabled: z.boolean().optional(),
  acknowledged: z.literal(true).optional(),
  /**
   * AC1: turning on a feature that reads documents needs the documents acknowledgement in the same request
   * (422 AI_DOCUMENTS_ACK_REQUIRED) until it is stamped at `AI_DOCUMENTS_ACKNOWLEDGEMENT_VERSION`.
   */
  documentsAcknowledged: z.literal(true).optional(),
  model: z.string().trim().max(100).nullable().optional(),
  monthlyTokenLimit: z.number().int().min(0).nullable().optional(),
  perUserDailyRuns: z.number().int().min(1).max(500).optional(),
  /** AE5a: the company assistant's everyday change cards. */
  assistantChanges: z.boolean().optional(),
  /** AE5b: the company assistant's sensitive change cards (ADR-051). */
  assistantSensitive: z.boolean().optional(),
  /** AE4': the company assistant reads photos and PDFs sent in its panel. */
  assistantDocuments: z.boolean().optional(),
  /** Only the features named change (zod 4's `record` over an enum would require every key). */
  features: z.partialRecord(z.enum(AI_FEATURES), z.boolean()).optional(),
  /** AA1: the company's area switches (true: on); only the areas named change, the ticks stay. */
  areas: z.partialRecord(z.enum(AI_AREAS), z.boolean()).optional(),
  retentionDays: z.number().int().min(1).max(365).optional(),
  providerZoneApproved: z.boolean().optional(),
})
export type AiSettingsPatch = z.input<typeof aiSettingsPatchSchema>

export interface AiSettingsState {
  value: AiSettings
  version: number
  /** Whether the company's key is set (never the key). */
  keySet: boolean
  keyUpdatedAt: string | null
  /** What the deployment and the console allow. */
  allowedModels: string[]
  consoleCap: number | null
  /** AA1: the areas the console allows this company (the others show as not allowed). Optional for fixtures. */
  allowedAreas?: AiArea[]
  /** The company's zone and whether the provider needs the zone approval. */
  zone: 'OM' | 'GLOBAL'
  zoneApprovalNeeded: boolean
  /** Why the assistant isn't available to anyone now, if it isn't. */
  blocked: AiUnavailableReason | null
}

// ── availability (`GET /ai/status`, any signed-in user) ──────────────────────

export const AI_UNAVAILABLE_REASONS = [
  /** The deployment has the assistant off (`AI_ENABLED`). */
  'disabled',
  /** The operator hasn't switched the `ai` module on for the company. */
  'module_off',
  'no_policy',
  'suspended',
  /** The company hasn't enabled it in Settings › AI assistant. */
  'not_enabled',
  'no_key',
  'residency',
  /** The user lacks `ai.use`, or is a system, public-link or impersonated actor. */
  'not_allowed',
] as const
export type AiUnavailableReason = (typeof AI_UNAVAILABLE_REASONS)[number]

export interface AiStatus {
  available: boolean
  reason: AiUnavailableReason | null
  /** The features on for the company and the module (the user still needs each subject's permission). */
  features: AiFeature[]
  model: string | null
  /** Runs left for the user today; null when not available. */
  runsLeftToday: number | null
  /**
   * AE4' (ASSISTANT_EVERYWHERE.md §7.6): the company assistant's panel takes photos and PDFs (its switch is on
   * and the company accepted the documents acknowledgement). Optional for fixtures (absent: no).
   */
  documents?: boolean
}

// ── conversations and messages ───────────────────────────────────────────────

/** `POST /ai/conversations` (ai.use + the subject's read permission in scope): one per user, feature and subject; an existing one is returned. */
export const aiConversationInputSchema = z.object({
  feature: z.enum(AI_FEATURES),
  subjectType: z.enum(AI_SUBJECT_TYPES),
  subjectId: z.uuid(),
})
export type AiConversationInput = z.input<typeof aiConversationInputSchema>

/** A record a message touched, as `ref:<type>:<id>` links; the label as the tool returned it. */
export interface AiRef {
  type: string
  id: string
  label: string
}

export interface AiMessage {
  id: string
  role: 'USER' | 'ASSISTANT'
  text: string
  refs: AiRef[]
  /** The run that produced it (assistant) or that it started (user). */
  runId: string | null
  /** A feature's structured result (status draft, plan, BOQ mapping), when the run returned one. */
  output: unknown | null
  /**
   * The figures of an answer that no tool output of its run contains (as `run.completed` sent them), so a
   * reload marks them "Unverified" too. Optional for fixtures.
   */
  unverified?: string[]
  /** The proposals of this message's run and how each ended (so a reload shows the outcome). */
  proposals?: AiProposal[]
  /** How this message's run ended (on the user's message when no answer followed). Optional for fixtures. */
  runStatus?: AiRunStatus | null
  /**
   * Why that run ended badly: an `AI_ERROR_CODES` code when it FAILED or was ABANDONED, `SUPERSEDED` when a
   * new question ended it while it waited for decisions; null otherwise. Optional for fixtures.
   */
  runErrorCode?: string | null
  /** AE4': on a user's message, what was read from the documents sent with it (absent or null: none). */
  reading?: AiMessageReading | null
  createdAt: string
}

export interface AiConversation {
  id: string
  feature: AiFeature
  subject: { type: AiSubjectType; id: string; label: string }
  title: string | null
  createdAt: string
  lastActivityAt: string
  /** Its text was purged (retention or deletion); only the outline remains. */
  purged: boolean
  messages: AiMessage[]
  /** A run waiting for decisions on its proposals. */
  paused: { runId: string; proposals: AiProposal[]; expiresAt: string } | null
}
export type AiConversationListItem = Omit<AiConversation, 'messages' | 'paused'> & { messageCount: number }

/** `GET /ai/conversations?feature&subjectType&subjectId`: the user's own, newest first. */
export const aiConversationsQuerySchema = pageQuerySchema.extend({
  feature: z.enum(AI_FEATURES).optional(),
  subjectType: z.enum(AI_SUBJECT_TYPES).optional(),
  subjectId: z.uuid().optional(),
})
export type AiConversationsQuery = z.input<typeof aiConversationsQuerySchema>

/**
 * `POST /ai/conversations/:id/messages`: answers `text/event-stream` (AiStreamEvent). Up to 8,000 characters
 * of a plan's scope with its start line (`aiPlanMessage`); the chat composer keeps to 4,000.
 */
export const aiMessageInputSchema = z.object({
  text: requiredText(AI_MESSAGE_MAX, 'Ask something'),
  language: z.enum(AI_LANGUAGES),
  /**
   * Context the screen adds without showing it in the user's bubble: for `projects.explain_signal`, the
   * alert asked about (checked by the server against the project's live signals; given to the model).
   */
  context: z
    .object({
      signal: z.object({ code: z.enum(SIGNAL_CODES), scopeKey: z.string().max(100) }).optional(),
      /**
       * AA1 `accounting.explain`: the report figure asked about (as `GET /accounting/drilldown` takes it); the
       * server checks it and gives the model its drill-down.
       */
      figure: z
        .object({
          report: z.enum(['profit_and_loss', 'balance_sheet', 'trial_balance', 'cash_flow', 'vat_return']),
          accountId: z.uuid().optional(),
          section: z.string().max(60).optional(),
          from: z.iso.date().optional(),
          to: z.iso.date().optional(),
          asOf: z.iso.date().optional(),
          compareFrom: z.iso.date().optional(),
          compareTo: z.iso.date().optional(),
        })
        .optional(),
      /** AA1 `accounting.review` and `accounting.commentary`: the period. */
      period: z.object({ from: z.iso.date(), to: z.iso.date() }).optional(),
      /** AA1 `accounting.coding` on the new-expense form: what the user typed so far. */
      expense: z
        .object({
          payee: z.string().max(200).nullable(),
          description: z.string().max(500).nullable(),
          amount: z.string().max(30).nullable(),
          expenseDate: z.iso.date().nullable(),
        })
        .optional(),
      /**
       * IA1, the inventory features: what the stock screen shows. `variantId`: the item asked about (required on
       * the first message of `inventory.explain`); `warehouseId`: the warehouse the screen is filtered to. The
       * server checks both and tells the model.
       */
      stock: z.object({ variantId: z.uuid().optional(), warehouseId: z.uuid().optional() }).optional(),
      /**
       * AC1 `purchasing.capture`: what the New purchase form already holds. `supplierId`: the supplier chosen
       * (the reader then keeps to it); `warehouseId`: where the goods are received. The server checks both.
       */
      capture: z.object({ supplierId: z.uuid().optional(), warehouseId: z.uuid().optional() }).optional(),
      /**
       * SA1, the service features: the site the screen shows (`siteId`) and the place open in its tree
       * (`placeId`). The server checks both (the company's site, a place of it) and tells the model.
       */
      site: z.object({ siteId: z.uuid().optional(), placeId: z.uuid().optional() }).optional(),
      /** AE2 `assistant.ask`: the page the question was asked on (`aiPageContextSchema`). */
      page: aiPageContextSchema.optional(),
    })
    .optional(),
})
export type AiMessageInput = z.input<typeof aiMessageInputSchema>

// ── documents a message may carry (AC1; ASSISTANT_CAPTURE.md §3.1) ───────────

/**
 * `POST /ai/conversations/:id/messages/documents`: multipart, the field `message` (a JSON string of
 * `aiMessageInputSchema`) then one to `AI_ATTACHMENT_MAX_FILES` parts named `file`. It answers the same event
 * stream as a message. The documents go to the model with the message and are dropped: the assistant stores
 * none of them. Types are told from the content, never the name.
 */
export const AI_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const
export type AiAttachmentType = (typeof AI_ATTACHMENT_TYPES)[number]
export const AI_ATTACHMENT_MAX_FILES = 4
/** Each document's size limit: 5 MiB. */
export const AI_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024
/** A PDF of more pages is refused (422 AI_DOCUMENT_TOO_LONG): a bill is a few pages; split a longer one. */
export const AI_ATTACHMENT_MAX_PDF_PAGES = 10
/** The features that take documents; any other answers 422 AI_ATTACHMENTS_NOT_ACCEPTED. */
export const AI_ATTACHMENT_FEATURES: readonly AiFeature[] = ['purchasing.capture', 'service.capture']
/** Whether the feature reads documents, so it needs the documents acknowledgement (AC1, decision D3). */
export const isAiDocumentFeature = (feature: AiFeature): boolean => AI_ATTACHMENT_FEATURES.includes(feature)
/**
 * Whether the company's documents acknowledgement is at `version` (by default the current one; an older
 * fixture: no).
 */
export function aiDocumentsAcknowledged(
  settings: { documentsAcknowledgement?: { version: number } | null },
  version: number = AI_DOCUMENTS_ACKNOWLEDGEMENT_VERSION,
): boolean {
  return (settings.documentsAcknowledgement?.version ?? 0) >= version
}

// ── what is read from a message's documents (AE4'; ASSISTANT_EVERYWHERE.md §7.6) ──

/** What a document is, from its title and wording. */
export const AI_READING_KINDS = [
  'INVOICE',
  'CREDIT_NOTE',
  'QUOTATION',
  'ORDER',
  'DELIVERY_NOTE',
  'RECEIPT',
  'STATEMENT',
  'PRICE_LIST',
  'OTHER',
] as const
export type AiReadingKind = (typeof AI_READING_KINDS)[number]
/** The most documents one reading returns (a PDF may hold several), and lines a document. */
export const AI_READING_MAX_DOCUMENTS = 8
export const AI_READING_MAX_LINES = 100

const readingLineSchema = z.object({
  /** The item as written, not translated. */
  description: z.string().max(300).nullable(),
  /** The issuer's own item code as written. */
  code: z.string().max(60).nullable(),
  quantity: z.string().max(30).nullable(),
  unit: z.string().max(30).nullable(),
  unitPrice: z.string().max(30).nullable(),
  discountPercent: z.string().max(10).nullable(),
  taxPercent: z.string().max(10).nullable(),
  /** The line's own total as printed. */
  lineTotal: z.string().max(30).nullable(),
})

export const aiReadingDocumentSchema = z.object({
  /** The files (1: the first sent) it was read from: a document may span photos, and a PDF may hold several. */
  files: z.array(z.number().int().min(1).max(AI_ATTACHMENT_MAX_FILES)).max(AI_ATTACHMENT_MAX_FILES),
  kind: z.enum(AI_READING_KINDS).nullable(),
  /** Who issued it and to whom, as written; the issuer's tax (VAT) number as written. */
  issuer: z.string().max(200).nullable(),
  issuerTaxNumber: z.string().max(60).nullable(),
  recipient: z.string().max(200).nullable(),
  /** Its own number. */
  reference: z.string().max(100).nullable(),
  /** YYYY-MM-DD as the reader understood them. */
  date: z.string().max(10).nullable(),
  dueDate: z.string().max(10).nullable(),
  currency: z.string().max(40).nullable(),
  /** Whether its unit prices include VAT; null: it cannot be told. */
  pricesIncludeTax: z.boolean().nullable(),
  net: z.string().max(30).nullable(),
  tax: z.string().max(30).nullable(),
  total: z.string().max(30).nullable(),
  lines: z.array(readingLineSchema).max(AI_READING_MAX_LINES),
  /** It has more lines than were returned. */
  truncated: z.boolean(),
  /** Other text worth keeping (payment terms, a delivery address, a note), as written. */
  otherText: z.string().max(1000).nullable(),
  /** What the reader could not read or was unsure of. */
  notes: z.array(z.string().max(300)).max(10),
})
export type AiReadingDocument = z.infer<typeof aiReadingDocumentSchema>

/**
 * The reading call's answer (every field present, null when not read, as strict model output needs). Nothing
 * here is trusted: it is what the documents say, and the cards check every id and figure.
 */
export const aiDocumentReadingOutputSchema = z.object({
  documents: z.array(aiReadingDocumentSchema).max(AI_READING_MAX_DOCUMENTS),
})
export type AiDocumentReadingOutput = z.infer<typeof aiDocumentReadingOutputSchema>

/** What a user's message keeps of its documents: the files as sent (never their bytes) and what was read. */
export interface AiMessageReading {
  /** The names as uploaded (labels, never trusted) and the types told from the content. */
  files: { name: string; contentType: AiAttachmentType }[]
  documents: AiReadingDocument[]
}

// ── proposals ────────────────────────────────────────────────────────────────

export const AI_PROPOSAL_STATUSES = [
  'PENDING',
  'APPROVED',
  'EXECUTED',
  'REJECTED',
  'EXPIRED',
  'FAILED',
] as const
export type AiProposalStatus = (typeof AI_PROPOSAL_STATUSES)[number]

/**
 * What a card shows: i18n keys and values, never sentences. `fields` are the editable arguments, each
 * with its label key and a kind the card renders (a date picker, a select, text).
 */
export interface AiProposalPreview {
  titleKey: string
  params: Record<string, string | number | null>
  fields: {
    key: string
    labelKey: string
    kind: 'text' | 'date' | 'money' | 'number' | 'select' | 'ref'
    value: string | number | null
    options?: { value: string; labelKey: string }[]
  }[]
  /**
   * AA1 (decision 31): the Books health finding a dismissal card is about, as Books health computes it for its
   * code and scope key over its period (`from`–`to`), with only the records the reader may open. Absent when
   * the finding no longer holds or isn't the reader's to see, and on older previews.
   */
  finding?: AiPreviewFinding
  /**
   * IA1 (INVENTORY_ASSISTANT.md decision 7): the lines of the draft a card would create (a draft transfer or a
   * draft purchase), as the server built them. Read-only on the card; the user edits the draft on its own page.
   */
  lines?: AiPreviewLine[]
  /**
   * AE5a: what a change card changes, item by item, as it is now and as it will be: prices (`money`) or
   * quantities on hand. Read-only on the card.
   */
  changes?: AiPreviewChange[]
  /**
   * AE5b (ADR-051): a sensitive change (it posts, voids, moves money or archives), set by the core from the
   * card's registration; the card says so.
   */
  sensitive?: boolean
}

/** One item of a change card (AE5a): its value now and after the change. */
export interface AiPreviewChange {
  label: string
  labelAr: string | null
  sku: string | null
  before: string | null
  after: string | null
  /** A price (OMR, 3 decimals), else a quantity in `unitSymbol`. */
  money: boolean
  unitSymbol: string | null
}

/** A line of a draft document on a proposal card. */
export interface AiPreviewLine {
  variantId: string
  label: string
  labelAr: string | null
  sku: string | null
  quantity: string
  unitSymbol: string
  /** A purchase line's unit price, on the document's price basis; null on a transfer. */
  unitPrice: string | null
  /** quantity × unitPrice (3 dp); null on a transfer. */
  amount: string | null
}

/** A dismissal card's finding: the Books health row's own parts, so the card reads it as that row does. */
export interface AiPreviewFinding {
  code: BookCheckCode
  severity: BookCheckSeverity
  area: BookCheckArea
  scopeKey: string
  params: Record<string, BookFindingParam>
  refs: { type: string; id: string; label: string; date?: string }[]
  since: string | null
  from: string
  to: string
}

export interface AiProposal {
  id: string
  tool: string
  args: Record<string, unknown>
  /** The user's edits, when made. */
  finalArgs: Record<string, unknown> | null
  preview: AiProposalPreview
  status: AiProposalStatus
  /** What it created or changed, once executed. */
  resultRef: AiRef | null
  errorCode: string | null
  expiresAt: string
}

/**
 * The cards one decision request takes, so the most a run may pause with: every pending card is decided at once
 * (a pause of more could never be decided; the run sends them back to the model to propose fewer).
 */
export const AI_DECISIONS_MAX = 10

/** `POST /ai/runs/:id/decisions`: answers `text/event-stream`; each pending proposal approved (optionally edited) or rejected. */
export const aiDecisionsSchema = z.object({
  decisions: z
    .array(
      z.object({
        proposalId: z.uuid(),
        approve: z.boolean(),
        finalArgs: z.record(z.string(), z.unknown()).optional(),
        /** A rejection's reason, passed to the model. */
        message: optionalText(1000),
      }),
    )
    .min(1)
    .max(AI_DECISIONS_MAX),
})
export type AiDecisionsInput = z.input<typeof aiDecisionsSchema>

// ── stream events ────────────────────────────────────────────────────────────

const refSchema = z.object({ type: z.string(), id: z.string(), label: z.string() })
const usageSchema = z.object({
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  totalTokens: z.number().int(),
})

/**
 * The events of a run's stream (§14.1). Links become clickable only with `run.completed`, whose sanitised
 * `finalText` replaces the deltas. After headers, errors come as `run.failed`.
 */
export const aiStreamEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('run.started'), runId: z.uuid(), conversationId: z.uuid(), model: z.string() }),
  z.object({ type: z.literal('text.delta'), text: z.string() }),
  z.object({ type: z.literal('tool.started'), callId: z.string(), tool: z.string(), labelKey: z.string() }),
  z.object({
    type: z.literal('tool.finished'),
    callId: z.string(),
    ok: z.boolean(),
    refs: z.array(refSchema),
  }),
  z.object({
    type: z.literal('proposal'),
    proposalId: z.uuid(),
    tool: z.string(),
    args: z.record(z.string(), z.unknown()),
    preview: z.custom<AiProposalPreview>(),
    expiresAt: z.string(),
  }),
  z.object({ type: z.literal('run.paused'), runId: z.uuid(), proposalIds: z.array(z.uuid()) }),
  /**
   * AE3 (`open_page`, `open_record`): the panel opens a page in the user's browser, through the router (an
   * unsaved form still asks first). `path` is one of `AI_PAGES` with the list's `search`; `ref`, a record the
   * conversation named, whose page the web knows by its type.
   */
  z.object({
    type: z.literal('screen.open'),
    path: z.string().nullable(),
    search: z.record(z.string(), z.string()).nullable(),
    ref: refSchema.nullable(),
  }),
  /** How a decided proposal ended (after `POST /ai/runs/:id/decisions`), before the run goes on. */
  z.object({
    type: z.literal('proposal.resolved'),
    proposalId: z.uuid(),
    status: z.enum(['EXECUTED', 'REJECTED', 'EXPIRED', 'FAILED']),
    resultRef: refSchema.nullable(),
    errorCode: z.string().nullable(),
  }),
  z.object({
    type: z.literal('run.completed'),
    messageId: z.uuid(),
    finalText: z.string(),
    refs: z.array(refSchema),
    /** A feature's structured result (status draft, plan, BOQ mapping). */
    output: z.unknown().optional(),
    /** Numbers in the text that no tool output of the run contains (status draft: highlighted "Unverified"). */
    unverified: z.array(z.string()).optional(),
    usage: usageSchema,
  }),
  z.object({
    type: z.literal('run.failed'),
    code: z.string(),
    retryable: z.boolean(),
    /** AI_BUDGET_EXCEEDED: whose limit ran out. */
    scope: z.enum(['tenant', 'user_daily']).optional(),
  }),
])
export type AiStreamEvent = z.infer<typeof aiStreamEventSchema>

// ── runs, usage, key (ai.manage) ─────────────────────────────────────────────

export const AI_RUN_STATUSES = [
  'RUNNING',
  'COMPLETED',
  'PAUSED',
  'FAILED',
  'CANCELLED',
  'ABANDONED',
  'EXPIRED',
] as const
export type AiRunStatus = (typeof AI_RUN_STATUSES)[number]

/** Run metadata for administrators: never the text (ASM-226). */
export interface AiRunMeta {
  id: string
  actor: { id: string; name: string; nameAr: string | null }
  feature: AiFeature
  subject: { type: AiSubjectType; id: string; label: string | null }
  model: string
  agent: { name: string; version: string }
  status: AiRunStatus
  errorCode: string | null
  tokens: { reserved: number; input: number; output: number; total: number }
  toolCalls: number
  startedAt: string
  endedAt: string | null
}
export const AI_RUN_SORT_FIELDS = ['startedAt', 'tokens', 'status'] as const
export const aiRunsQuerySchema = pageQuerySchema.extend({
  status: z.enum(AI_RUN_STATUSES).optional(),
  feature: z.enum(AI_FEATURES).optional(),
  userId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type AiRunsQuery = z.input<typeof aiRunsQuerySchema>

/** `GET /ai/usage?month=YYYY-MM`. */
export const aiUsageQuerySchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'A month as YYYY-MM')
    .optional(),
})
export interface AiUsage {
  month: string
  /** The effective limit: min(company limit, console cap). */
  limit: number | null
  used: number
  /** Reserved by running and paused runs. */
  reserved: number
  byDay: { date: string; tokens: number; runs: number }[]
  byFeature: { feature: AiFeature; tokens: number; runs: number }[]
  byUser: { user: { id: string; name: string; nameAr: string | null }; tokens: number; runs: number }[]
}

/** `POST /ai/key-check` (ai.manage, a few a minute): one cheap provider call with the company's key. */
export interface AiKeyCheck {
  ok: boolean
  /** The allowed models the key can use. */
  models: string[]
}

// ── console policy (`GET/PUT /platform/tenants/:id/ai-policy`) ───────────────

/** Written by the super-admin console; the runtime reads its own row through a view. */
export const tenantAiPolicyInputSchema = z.object({
  /** Tokens a month; the company can set its own limit at or below it. */
  monthlyTokenCap: z.number().int().min(0).max(10_000_000_000),
  /** Empty: every model of the deployment's `AI_MODELS`. */
  allowedModels: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
  suspended: z.boolean().default(false),
  suspendedReason: optionalText(500),
  /**
   * AA1: the areas the company may use (Projects, Accounting; IA1: Inventory; AC1: Purchasing). Omitted on
   * update: kept (never re-opened by leaving it out); a new policy starts with all. Empty: none.
   */
  allowedAreas: z.array(z.enum(AI_AREAS)).max(AI_AREAS.length).optional(),
  version: versionSchema.optional(),
})
export type TenantAiPolicyInput = z.input<typeof tenantAiPolicyInputSchema>
export interface TenantAiPolicy {
  tenantId: string
  monthlyTokenCap: number
  allowedModels: string[]
  suspended: boolean
  suspendedReason: string | null
  updatedAt: string
  version: number
  /** This month's use, for the operator. */
  usedThisMonth: number
  /** The deployment's `AI_MODELS`, for the allowed-models checklist. Optional for fixtures. */
  deploymentModels?: string[]
  /** AA1: the areas allowed (Projects, Accounting; IA1: Inventory). Optional for fixtures (absent: all). */
  allowedAreas?: AiArea[]
}
/** The cap a policy row starts with when the operator switches `ai` on. */
export const DEFAULT_AI_MONTHLY_TOKEN_CAP = 1_000_000

// ── feature outputs (structured results of the runs) ─────────────────────────

/** `projects.status_draft`: the four sections; the key figures come from the week's record, never the model. */
export const aiStatusDraftOutputSchema = z.object({
  summary: z.string().max(4000),
  progress: z.string().max(4000),
  risksIssues: z.string().max(4000),
  nextSteps: z.string().max(4000),
})
export type AiStatusDraftOutput = z.infer<typeof aiStatusDraftOutputSchema>

/** `projects.plan`: a proposed plan the user edits before `POST /projects/:id/plan/apply`. Offsets in working days. */
export const aiPlanOutputSchema = z
  .object({
    phases: z
      .array(
        z.object({
          name: z.string().max(200),
          nameAr: z.string().max(200).nullable(),
          offsetWd: z.number().int().min(0).max(2000),
          durationWd: z.number().int().min(1).max(2000),
          weight: z.number().min(0).max(100).nullable(),
          milestones: z
            .array(
              z.object({
                name: z.string().max(200),
                nameAr: z.string().max(200).nullable(),
                offsetWd: z.number().int().min(0).max(2000),
              }),
            )
            .max(10),
          tasks: z
            .array(
              z.object({
                title: z.string().max(200),
                offsetWd: z.number().int().min(0).max(2000).nullable(),
              }),
            )
            .max(30),
        }),
      )
      .max(20),
    assumptions: z.array(z.string().max(300)).max(10),
  })
  .refine((v) => v.phases.reduce((n, p) => n + p.tasks.length, 0) <= 200, {
    path: ['phases'],
    message: 'At most 200 tasks in a plan',
  })
export type AiPlanOutput = z.infer<typeof aiPlanOutputSchema>

/** `projects.boq_mapping`: which import field each header is, with a confidence; checked deterministically after. */
export const aiBoqMappingOutputSchema = z.object({
  columns: z
    .array(
      z.object({
        /** The header as in the file. */
        column: z.string().max(200),
        /** A `BOQ_CSV_COLUMNS` key, or null: not imported. */
        field: z.string().max(40).nullable(),
        confidence: z.number().min(0).max(1),
      }),
    )
    .max(40),
})
export type AiBoqMappingOutput = z.infer<typeof aiBoqMappingOutputSchema>

// ── errors ───────────────────────────────────────────────────────────────────

export const AI_ERROR_CODES = [
  /** 503: the deployment has the assistant off. */
  'AI_DISABLED',
  /** 422 `{reason}`: suspended, no_policy, not_enabled, feature_off, residency, area_off (AA1). */
  'AI_BLOCKED_BY_POLICY',
  /** 422: the company's OpenAI key isn't set. */
  'AI_KEY_MISSING',
  /** 422: turning the assistant on needs the acknowledgement. */
  'AI_ACK_REQUIRED',
  /** 422 (AC1): turning on a feature that reads documents needs the documents acknowledgement. */
  'AI_DOCUMENTS_ACK_REQUIRED',
  /** 422: the company's limit is above the console cap, or the model isn't allowed. */
  'AI_LIMIT_ABOVE_CAP',
  'AI_MODEL_NOT_ALLOWED',
  /** 422 `{scope: 'tenant' | 'user_daily'}`. */
  'AI_BUDGET_EXCEEDED',
  /** 409: the user already has a run going. */
  'AI_RUN_IN_PROGRESS',
  /** 409: the paused run expired, or the agent changed since. */
  'AI_RUN_EXPIRED',
  'AI_RESUME_INCOMPATIBLE',
  /** 403: system, public-link and impersonated actors never run the assistant. */
  'AI_NOT_ALLOWED',
  /** Provider errors (stream `run.failed` codes, or 422 before the stream). */
  'AI_PROVIDER_AUTH',
  'AI_PROVIDER_UNAVAILABLE',
  'AI_PROVIDER_RATE_LIMITED',
  'AI_TURN_LIMIT',
  'AI_MODEL_ERROR',
  'AI_TIMEOUT',
  /** 422: a plan to apply has more than 20 phases or 200 tasks. */
  'PLAN_TOO_LARGE',
  /** 422 (AC1): the feature takes no documents; more documents than `AI_ATTACHMENT_MAX_FILES`. */
  'AI_ATTACHMENTS_NOT_ACCEPTED',
  'AI_TOO_MANY_ATTACHMENTS',
  /** 422 (AC1 review): a PDF of more than `AI_ATTACHMENT_MAX_PDF_PAGES` pages. `{pages}`. */
  'AI_DOCUMENT_TOO_LONG',
  /**
   * 422 (AI_PDF_INPUT=images): a PDF whose pages could not be drawn as pictures for the model.
   * `{reason: 'damaged' | 'password' | 'too_large'}`.
   */
  'AI_DOCUMENT_UNREADABLE',
  /** 503 (AC1 review): the server is reading as many documents as it holds at once; retryable in a minute. */
  'AI_BUSY',
] as const
export type AiErrorCode = (typeof AI_ERROR_CODES)[number]

/** Why a run ended (`AiRunMeta.errorCode`) besides the stream codes of `AI_ERROR_CODES`. */
export const AI_RUN_END_CODES = [
  /** A new question in the conversation replaced the paused run. */
  'SUPERSEDED',
  /** The conversation's text was purged (retention, deletion, privacy). */
  'PURGED',
] as const
/** Why a proposal failed (`AiProposal.errorCode`), besides the command's own problem codes. */
export const AI_PROPOSAL_ERROR_CODES = [
  'UNKNOWN_TOOL',
  'INVALID_INPUT',
  'NOT_CONFIRMED',
  /** The process stopped between the approval and the command. */
  'INTERRUPTED',
  'ERROR',
] as const

// ── accounting assistant outputs (AA1) ───────────────────────────────────────

/** `accounting.commentary`: a monthly management commentary from the books; numbers no tool returned are marked. */
export const aiCommentaryOutputSchema = z.object({
  summary: z.string().max(4000),
  revenue: z.string().max(4000),
  costs: z.string().max(4000),
  cash: z.string().max(4000),
  watch: z.string().max(4000),
})
export type AiCommentaryOutput = z.infer<typeof aiCommentaryOutputSchema>

/**
 * `accounting.coding` on the new-expense form: the suggested category (and account when it differs from the
 * category's), with a confidence and the reason in a sentence. It fills the form; the user saves.
 */
export const aiExpenseCodingOutputSchema = z.object({
  categoryId: z.uuid().nullable(),
  accountCode: z.string().max(40).nullable(),
  taxRateId: z.uuid().nullable(),
  confidence: z.number().min(0).max(1),
  reason: z.string().max(300),
})
export type AiExpenseCodingOutput = z.infer<typeof aiExpenseCodingOutputSchema>

/**
 * `purchasing.capture` (AC1): what was read from a list or a supplier's bill, as `POST
 * /purchasing/purchases/prefill` takes it. The numbers are as written; rules parse and check them.
 */
export const aiPurchaseCaptureOutputSchema = purchaseCaptureSchema
export type AiPurchaseCaptureOutput = z.infer<typeof aiPurchaseCaptureOutputSchema>

/**
 * `service.capture` (SA1): the equipment read from a list or a document, as rows for the site's CSV import
 * (`siteCaptureToCsv`); the import's preview checks them.
 */
export const aiServiceCaptureOutputSchema = serviceSiteCaptureSchema
export type AiServiceCaptureOutput = z.infer<typeof aiServiceCaptureOutputSchema>

/** At most this many rows and columns in a table inside an answer (ACCOUNTING_ASSISTANT.md decision 11). */
export const AI_TABLE_MAX_ROWS = 20
export const AI_TABLE_MAX_COLUMNS = 8
